// src/components/PurchaseFlow/Steps/DataEntry/Sections/BankInformation.js
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { faCheckCircle, faExclamationTriangle, faBuildingColumns, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BankSelectionModal from '../../../../Modals/BankSelectionModal';

const BankInformation = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    sectionValidity,
    setSectionValidity,
    handleSectionClick,
    bankData, // Datos del banco seleccionado previamente
    onBankChange, // Callback para cambiar el banco
    clientData, // Datos del cliente para el modal
    displayMode = 'accordion'
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [showBankModal, setShowBankModal] = useState(false);

  const requiredFields = ['cbu'];

  useEffect(() => {
    const allFieldsTouched = requiredFields.every(field => touchedFields[field]);
    if (allFieldsTouched) {
      validateSection();
    }
    // eslint-disable-next-line
  }, [touchedFields]);

  useEffect(() => {
    // Validar automáticamente si hay datos del banco
    if (bankData && formData.cbu) {
      validateSection();
    }
  }, [bankData, formData.cbu]);

  const validateCBU = (cbu) => {
    // Validación básica del CBU - 22 dígitos
    if (!cbu) {
      return 'El CBU es requerido';
    }
    
    // Eliminar espacios y guiones
    const cleanCBU = cbu.replace(/[\s-]/g, '');
    
    if (!/^\d{22}$/.test(cleanCBU)) {
      return 'El CBU debe tener exactamente 22 dígitos';
    }

    // TODO: Aquí se agregará el algoritmo de validación específico del CBU
    // Por ahora solo validamos formato básico
    
    return '';
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'cbu':
        error = validateCBU(value);
        break;
      default:
        break;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));

    return !error;
  };

  const validateSection = () => {
    // Verificar que tengamos banco seleccionado
    if (!bankData || !bankData.id) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    // Validar CBU
    const cbuValid = validateField('cbu', formData.cbu);

    if (!cbuValid) {
      setSectionValidity((prev) => ({ ...prev, [sectionName]: false }));
      return false;
    }

    setSectionValidity((prev) => ({ ...prev, [sectionName]: true }));
    return true;
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    
    // Para CBU, solo permitir dígitos y formatear
    if (name === 'cbu') {
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length <= 22) {
        handleChange({ target: { name, value: cleanValue } });
      }
    } else {
      handleChange(e);
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const formatCBU = (cbu) => {
    if (!cbu) return '';
    // Formatear CBU en grupos de 4 dígitos para mejor legibilidad
    const cleanCBU = cbu.replace(/\D/g, '');
    return cleanCBU.replace(/(\d{4})(?=\d)/g, '$1-');
  };

  const handleBankChangeClick = () => {
    setShowBankModal(true);
  };

  const handleBankConfirm = (clientWithBank) => {
    // Actualizar el banco en el estado local del componente padre
    if (onBankChange) {
      onBankChange(clientWithBank.bank);
    }
    setShowBankModal(false);
  };

  const handleBankModalClose = () => {
    setShowBankModal(false);
  };

  // Verificar si se permite editar la información bancaria
  const allowEditBankInfo = process.env.REACT_APP_ALLOW_EDIT_BANK_INFO === 'true';

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <h5 className="d-flex align-items-center mb-0 fw-light">
            <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
            Información Bancaria
            {sectionValidity[sectionName] === true && (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
            )}
            {sectionValidity[sectionName] === false && (
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
            )}
          </h5>
        </div>
        <div className="section-content">
          <div className="row">
            {/* Banco seleccionado (readonly) */}
            <div className="col-md-6 mb-3">
              <label htmlFor="selectedBank" className="form-label">
                Banco seleccionado <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="selectedBank"
                  value={bankData ? bankData.name : 'No se ha seleccionado un banco'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                {allowEditBankInfo && (
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={handleBankChangeClick}
                    title="Cambiar banco"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </div>
              {!bankData && (
                <div className="form-text text-danger">
                  Debe seleccionar un banco antes de continuar
                </div>
              )}
              {bankData && allowEditBankInfo && (
                <div className="form-text text-muted">
                  Puede cambiar el banco haciendo clic en el botón de editar
                </div>
              )}
              {bankData && !allowEditBankInfo && (
                <div className="form-text text-muted">
                  Banco seleccionado confirmado
                </div>
              )}
            </div>

            {/* CBU */}
            <div className="col-md-6 mb-3">
              <label htmlFor="cbu" className="form-label">
                CBU <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className={`form-control ${errors.cbu ? 'is-invalid' : touchedFields.cbu && !errors.cbu ? 'is-valid' : ''}`}
                  id="cbu"
                  name="cbu"
                  value={formData.cbu || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  placeholder="Ingrese su CBU (22 dígitos)"
                  maxLength="22"
                />
                {errors.cbu && (
                  <div className="invalid-feedback">
                    {errors.cbu}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal de selección de banco */}
        {showBankModal && clientData && (
          <BankSelectionModal
            client={clientData}
            onConfirm={handleBankConfirm}
            onClose={handleBankModalClose}
            loading={false}
          />
        )}
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
          Información Bancaria{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        id={`collapse${sectionName}`}
      >
        <div className="accordion-body">
          <div className="row">
            {/* Banco seleccionado (readonly) */}
            <div className="col-md-6 mb-3">
              <label htmlFor="selectedBank" className="form-label">
                Banco seleccionado <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  id="selectedBank"
                  value={bankData ? bankData.name : 'No se ha seleccionado un banco'}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
                {allowEditBankInfo && (
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={handleBankChangeClick}
                    title="Cambiar banco"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                )}
              </div>
              {!bankData && (
                <div className="form-text text-danger">
                  Debe seleccionar un banco antes de continuar
                </div>
              )}
              {bankData && allowEditBankInfo && (
                <div className="form-text text-muted">
                  Puede cambiar el banco haciendo clic en el botón de editar
                </div>
              )}
              {bankData && !allowEditBankInfo && (
                <div className="form-text text-muted">
                  Banco seleccionado confirmado
                </div>
              )}
            </div>

            {/* CBU */}
            <div className="col-md-6 mb-3">
              <label htmlFor="cbu" className="form-label">
                CBU <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                </span>
                <input
                  type="text"
                  className={`form-control ${errors.cbu ? 'is-invalid' : touchedFields.cbu && !errors.cbu ? 'is-valid' : ''}`}
                  id="cbu"
                  name="cbu"
                  value={formData.cbu || ''}
                  onChange={handleFieldChange}
                  onBlur={handleFieldBlur}
                  placeholder="Ingrese su CBU (22 dígitos)"
                  maxLength="22"
                />
                {errors.cbu && (
                  <div className="invalid-feedback">
                    {errors.cbu}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de selección de banco */}
      {showBankModal && clientData && (
        <BankSelectionModal
          client={clientData}
          onConfirm={handleBankConfirm}
          onClose={handleBankModalClose}
          loading={false}
        />
      )}
    </div>
  );
});

export default BankInformation;
